import { useEffect } from "react";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { SerializeFrom } from "@remix-run/node";
import { useParams } from "@remix-run/react";
import { cacheHeader } from "pretty-cache-header";

import { useFetch } from "~/hooks/useFetch";
import { createStateManager } from "~/modules/StateManager";
import { useStateManager } from "~/modules/StateManager.hooks";
import { createServerStateManager, ServerStateManager } from "~/modules/StateManager.server";
import { getMeetingTypes } from "~/services/schedulingRules.server";

export async function loader(args: LoaderFunctionArgs) {
  const currentState = await createServerStateManager(args);

  const searchParams = new URL(args.request.url).searchParams;
  const state = createStateManager({ searchParams }).setState((draft) => {
    draft.config.language = currentState.state.config.language;
  }).state;

  const stateManager = new ServerStateManager(args, state);
  const meetingTypes = await getMeetingTypes(stateManager.getSRSParams());

  return json(meetingTypes, {
    headers: {
      "Cache-Control": cacheHeader({ public: true, maxAge: "5minutes" }),
    },
  });
}

type UseMeetingTypesArgs =
  | { officeId: string; subjectId?: never; employeeId?: never }
  | { subjectId: string; officeId?: never; employeeId?: never }
  | { employeeId: string; subjectId?: never; officeId?: never };

export function useMeetingTypes(props: UseMeetingTypesArgs) {
  const { enterprise } = useParams();

  const fetcher = useFetch<SerializeFrom<typeof loader>>(
    `/api/enterprises/${enterprise}/meeting-types`,
  );

  const {
    config: { leadSegment, listing, meetingTypes },
    employeeId: paramEmployeeId,
    meetingType,
    officeId: paramOfficeId,
    subjectId: paramSubjectId,
  } = useStateManager().state;

  const officeId = props.officeId ?? paramOfficeId;
  const subjectId = props.subjectId ?? paramSubjectId;
  const employeeId = props.employeeId ?? paramEmployeeId;

  useEffect(() => {
    if (!meetingType) {
      const params = createStateManager({
        state: {
          officeId,
          subjectId,
          employeeId,
          config: { leadSegment, listing, meetingTypes },
        },
      }).toString();

      fetcher.submit(params);
    }
    // We only want to run this once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return fetcher.data ?? [];
}
